import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'app/store';
import {
  fetchRoles,
  createRole,
  updateRole,
  deleteRole,
  clearError
} from '../../slices/NdisSlices/platformAdmin/ManageRolesSlice';
import { Role } from '../../types/ndisSection/manageRolesTypes';

const ManageRoles: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch<AppDispatch>();
  const { roles, loading, error } = useSelector((state: RootState) => state.manageRoles);

  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentRole, setCurrentRole] = useState<Role | null>(null);
  const [roleLabel, setRoleLabel] = useState('');
  const [isMasterRole, setIsMasterRole] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });

  useEffect(() => {
    dispatch(fetchRoles());
  }, [dispatch]);

  const handleOpenDialog = (role: Role | null = null) => {
    setCurrentRole(role);
    setRoleLabel(role ? role.label : '');
    setIsMasterRole(role?.isMasterRole ?? false);
    setOpenDialog(true);
    dispatch(clearError());
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentRole(null);
    setRoleLabel('');
    setIsMasterRole(false);
    dispatch(clearError());
  };

  const handleOpenDeleteDialog = (role_id: string) => {
    setRoleToDelete(role_id);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setRoleToDelete(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleSaveRole = async () => {
    if (roleLabel.trim() === '') return;

    try {
      if (currentRole) {
        await dispatch(
          updateRole({
            role_id: currentRole.role_id,
            label: roleLabel,
            isMasterRole
          })
        ).unwrap();
        setSnackbar({
          open: true,
          message: 'Role updated successfully!',
          severity: 'success'
        });
      } else {
        await dispatch(createRole({ label: roleLabel, isMasterRole })).unwrap();
        setSnackbar({
          open: true,
          message: 'Role created successfully!',
          severity: 'success'
        });
      }
      handleCloseDialog();
    } catch (err: any) {
      console.error('Error saving role:', err);
      setSnackbar({
        open: true,
        message: error || err.message || 'An error occurred',
        severity: 'error'
      });
    }
  };

  const handleConfirmDelete = async () => {
    if (!roleToDelete) return;

    try {
      await dispatch(deleteRole(roleToDelete)).unwrap();
      setSnackbar({
        open: true,
        message: 'Role deleted successfully!',
        severity: 'success'
      });
    } catch (err: any) {
      console.error('Error deleting role:', err);
      setSnackbar({
        open: true,
        message: error || err.message || 'Failed to delete role',
        severity: 'error'
      });
    } finally {
      handleCloseDeleteDialog();
    }
  };

  return (
    <Box sx={{ p: isMobile ? 1 : 2, maxWidth: '100%', overflowX: 'auto' }}>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearError())}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: isMobile ? 1 : 2
          }}
        >
          <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="bold">
            Manage Roles
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            size={isMobile ? 'small' : 'medium'}
            sx={{ py: isMobile ? 0.5 : 1 }}
            disabled={loading}
          >
            Add Role
          </Button>
        </Box>

        {loading && roles.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
            <Table size={isMobile ? 'small' : 'medium'}>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    '& th': {
                      color: theme.palette.primary.contrastText,
                      fontWeight: 'bold',
                      fontSize: isMobile ? '0.875rem' : '1rem',
                      py: isMobile ? 1 : 2,
                      borderBottom: `2px solid ${theme.palette.primary.dark}`
                    }
                  }}
                >
                  <TableCell sx={{ fontWeight: 700, letterSpacing: '0.5px' }}>Role Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, letterSpacing: '0.5px' }}>
                    Master Role
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', fontWeight: 700, letterSpacing: '0.5px' }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roles.map((role) => (
                  <TableRow key={role.role_id} hover>
                    <TableCell sx={{ py: isMobile ? 0.5 : 1 }}>{role.label}</TableCell>
                    <TableCell sx={{ py: isMobile ? 0.5 : 1 }}>
                      {role.isMasterRole ? 'Yes' : 'No'}
                    </TableCell>
                    <TableCell sx={{ py: isMobile ? 0.5 : 1, textAlign: 'right' }}>
                      <IconButton
                        size={isMobile ? 'small' : 'medium'}
                        onClick={() => handleOpenDialog(role)}
                        color="primary"
                        disabled={loading}
                      >
                        <Edit fontSize={isMobile ? 'small' : 'medium'} />
                      </IconButton>
                      <IconButton
                        size={isMobile ? 'small' : 'medium'}
                        onClick={() => handleOpenDeleteDialog(role.role_id)}
                        color="error"
                        disabled={loading}
                      >
                        <Delete fontSize={isMobile ? 'small' : 'medium'} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Dialog for Add/Edit Role */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="xs" fullWidth>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearError())}>
              {error}
            </Alert>
          )}
          <DialogTitle>{currentRole ? 'Edit Role' : 'Add New Role'}</DialogTitle>
          <DialogContent sx={{ p: isMobile ? 1 : 2 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Role Name"
              fullWidth
              value={roleLabel}
              onChange={(e) => setRoleLabel(e.target.value)}
              variant="outlined"
              size={isMobile ? 'small' : 'medium'}
              disabled={loading}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={isMasterRole}
                  onChange={(e) => setIsMasterRole(e.target.checked)}
                  disabled={loading}
                />
              }
              label="Master Role"
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions sx={{ p: isMobile ? 1 : 2 }}>
            <Button
              onClick={handleCloseDialog}
              size={isMobile ? 'small' : 'medium'}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveRole}
              variant="contained"
              size={isMobile ? 'small' : 'medium'}
              disabled={loading || roleLabel.trim() === ''}
            >
              {loading ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} maxWidth="xs" fullWidth>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent sx={{ p: isMobile ? 1 : 2 }}>
            <Typography>
              Are you sure you want to delete this role? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: isMobile ? 1 : 2 }}>
            <Button
              onClick={handleCloseDeleteDialog}
              size={isMobile ? 'small' : 'medium'}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              color="error"
              size={isMobile ? 'small' : 'medium'}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </motion.div>
    </Box>
  );
};

export default ManageRoles;
